import { ExternalLink } from "lucide-react";
import { useState } from "react";

import type { FeedEvent } from "../../shared/types";
import { proxiedImageUrl } from "../lib/imageProxy";
import { navigate } from "../router";
import { metaFor } from "./EventTypeIcon";
import { PriceTag } from "./PriceTag";
import { StockBadge } from "./StockBadge";

function timeAgo(ts: number): string {
	const d = Date.now() - ts;
	if (d < 60_000) return `${Math.max(1, Math.floor(d / 1000))}s ago`;
	if (d < 3_600_000) return `${Math.floor(d / 60_000)}m ago`;
	if (d < 86_400_000) return `${Math.floor(d / 3_600_000)}h ago`;
	return new Date(ts).toLocaleDateString();
}

type Payload = {
	storeId?: string;
	title?: string;
	url?: string;
	imageUrl?: string | null;
	priceMinor?: number | null;
	prevPriceMinor?: number | null;
	inStock?: boolean;
	stockLevel?: number | null;
	message?: string;
};

async function startBuy(productId: number) {
	const res = await fetch("/api/buy", {
		method: "POST",
		headers: { "content-type": "application/json" },
		body: JSON.stringify({ productId, qty: 1 }),
	});
	const data = (await res.json()) as { jobId?: number; error?: string };
	if (data.jobId) {
		navigate(`/buy/${data.jobId}`);
	} else {
		alert(data.error ?? "Failed to enqueue buy job");
	}
}

function deltaPct(curr: number, prev: number): number {
	if (prev === 0) return 0;
	return Math.round(((curr - prev) / prev) * 100);
}

export function FeedItem({ event, isFresh }: { event: FeedEvent; isFresh?: boolean }) {
	const p = (event.payload ?? {}) as Payload;
	const meta = metaFor(event.type);
	const Icon = meta.Icon;
	const [busy, setBusy] = useState(false);

	const canBuy =
		event.productId != null && p.inStock !== false && event.type !== "removed" && event.type !== "adapter_error";

	const onBuy = async () => {
		if (event.productId == null) return;
		setBusy(true);
		try {
			await startBuy(event.productId);
		} finally {
			setBusy(false);
		}
	};

	const goToDetail = () => {
		if (event.productId != null) navigate(`/items/${event.productId}`);
	};

	const titleText = p.title ?? p.message ?? `event #${event.id}`;
	const prev = p.prevPriceMinor;
	const curr = p.priceMinor;
	const showDelta = prev != null && curr != null && prev !== curr && prev > 0;
	const pct = showDelta ? deltaPct(curr, prev) : 0;
	const deltaSign = pct < 0 ? "−" : "+";
	// Whether the detail cell has anything to say. Without this an event with
	// no stock, price or action still rendered an empty row and its gap.
	const hasDetail = typeof p.inStock === "boolean" || curr != null || !!p.url || canBuy;

	return (
		// One line per event where the list is wide enough, three stacked lines
		// where it isn't — decided by the list's own width (container query), not
		// the viewport's, so the same row fits the Stream and the narrow Needs-you
		// column beside it.
		<li
			className={`grid grid-cols-[36px_minmax(0,1fr)_auto] items-center gap-x-2.5 gap-y-0.5 px-2.5 py-2 transition-colors [grid-template-areas:'thumb_meta_time'_'thumb_title_title'_'thumb_detail_detail'] hover:bg-raised @2xl:grid-cols-[28px_150px_minmax(0,1fr)_auto_48px] @2xl:py-1 @2xl:[grid-template-areas:'thumb_meta_title_detail_time'] ${
				isFresh ? "bg-raised shadow-[inset_2px_0_0_var(--ink)]" : ""
			}`}
		>
			<div className="self-start [grid-area:thumb] @2xl:self-center">
				<Thumbnail src={p.imageUrl ?? null} alt={titleText} />
			</div>
			<div className="flex min-w-0 items-center gap-1.5 [grid-area:meta]">
				<span
					className={`inline-flex shrink-0 items-center gap-1 rounded-full px-1.5 py-px text-[11px] font-medium ${meta.chip}`}
				>
					<Icon className="size-3" strokeWidth={2.25} aria-hidden />
					{meta.label}
				</span>
				{p.storeId && <span className="truncate text-[11px] text-muted capitalize">{p.storeId}</span>}
			</div>
			<button
				type="button"
				disabled={event.productId == null}
				onClick={goToDetail}
				className="min-w-0 truncate text-left text-[13px] font-medium text-ink [grid-area:title] enabled:hover:underline disabled:cursor-default"
				title={titleText}
			>
				{titleText}
			</button>
			<span
				className="text-right text-[11px] text-muted [grid-area:time]"
				title={new Date(event.createdAt).toLocaleString()}
			>
				{timeAgo(event.createdAt)}
			</span>
			{hasDetail && (
				<div className="flex min-w-0 flex-wrap items-center gap-x-2.5 gap-y-1 [grid-area:detail] @2xl:flex-nowrap @2xl:justify-end">
					{typeof p.inStock === "boolean" && (
						<span className="@2xl:w-24">
							<StockBadge inStock={p.inStock} level={p.stockLevel} />
						</span>
					)}
					<span className="flex items-baseline gap-1.5 @2xl:w-40 @2xl:justify-end">
						{prev != null && curr != null && prev !== curr && (
							<span className="text-[11px] text-muted line-through">
								<PriceTag minor={prev} />
							</span>
						)}
						{showDelta && (
							<span
								className={`text-[11px] font-semibold ${pct < 0 ? "text-drop" : "text-needs"}`}
								title={`Previous ${(prev / 100).toLocaleString()} → ${(curr / 100).toLocaleString()}`}
							>
								{deltaSign}
								{Math.abs(pct)}%
							</span>
						)}
						{curr != null && <PriceTag minor={curr} />}
					</span>
					<span className="ml-auto flex items-center gap-1 @2xl:ml-0 @2xl:w-[92px] @2xl:justify-end">
						{p.url && (
							<a
								href={p.url}
								target="_blank"
								rel="noreferrer noopener"
								title="Open at store"
								aria-label="Open at store"
								className="flex size-tap items-center justify-center rounded-md text-muted hover:bg-paper hover:text-ink @2xl:size-7"
							>
								<ExternalLink className="size-3.5" aria-hidden />
							</a>
						)}
						{canBuy && (
							// Foil marks the controls that spend money. In a list where
							// most rows can be bought it's an outline, so the column
							// doesn't turn into a stripe of gradient; a hover fills it.
							<button
								onClick={onBuy}
								disabled={busy}
								className="inline-flex min-h-tap items-center rounded-md border border-foil px-3 text-xs font-semibold text-ink hover:bg-foil disabled:opacity-50 @2xl:h-7 @2xl:min-h-0"
							>
								{busy ? "Queueing…" : "Buy"}
							</button>
						)}
					</span>
				</div>
			)}
		</li>
	);
}

function Thumbnail({ src, alt }: { src: string | null; alt: string }) {
	const [failed, setFailed] = useState(false);
	const resolved = proxiedImageUrl(src);
	// Nothing to show and nothing ever will be: hold the column so titles stay
	// aligned down the list, but claim no height. The bordered icon well that
	// used to sit here restated the event chip a few pixels to its right, and
	// set a 64px floor under rows whose whole content was one line.
	if (!resolved) {
		return <div className="size-7" aria-hidden />;
	}
	// There was a picture and it failed. Keep the space it already claimed:
	// collapsing now would shrink a row the reader is looking at, which is the
	// same jump as an image loading late, in the other direction.
	if (failed) {
		return <div className="size-9 rounded-md bg-plate @2xl:size-7" aria-hidden />;
	}
	return (
		<img
			src={resolved}
			alt={alt}
			loading="lazy"
			onError={() => setFailed(true)}
			className="size-9 rounded-md bg-plate object-contain p-0.5 @2xl:size-7"
		/>
	);
}
